// Browser-local email cache backed by IndexedDB (via Dexie).
//
// Privacy principle: email content lives only in the provider mailbox and in
// this browser-local cache — it is never transmitted to or stored on our
// backend. Firestore receives only content-free categorization metadata.
//
// This is a REBUILDABLE CACHE, never a source of truth: the provider mailbox
// holds the content, Firestore holds the categorization. Any wipe (clear site
// data, browser eviction, new device) degrades to "sync again".

import Dexie from 'dexie';

const db = new Dexie('patensys-email');

db.version(1).stores({
  // id (PK) = provider message id; raw = full provider message object so all
  // existing provider accessors (getBody, getSubjectLine, ...) work unchanged.
  messages: 'id, threadId, date, fromAddress',
  meta: 'key',
});

// Bump when matching inputs change (rule sets, horizons, etc.) so cached
// messages are re-processed. Manual processing marks messages with this.
// v2: attachment scanning (stage 0) added — re-scan the cached corpus so
// previously unmatched messages get filename/attachment-content matching.
const PROCESSED_VERSION = 2;

async function saveMessages(entries) {
  // entries: [{id, threadId, date, fromAddress, subject, raw}]
  if (!entries || entries.length === 0) {
    return;
  }
  const rows = entries
    .filter((e) => e && e.id)
    .map((e) => ({ ...e, processedVersion: e.processedVersion ?? null }));
  // bulkPut = upsert; re-synced messages update in place (dedupe by id).
  // Preserve the existing processedVersion so incremental syncs (which overlap
  // at the boundary by design) don't knock processed messages back into the
  // unprocessed backlog.
  await db.transaction('rw', db.messages, async () => {
    const existing = await db.messages.bulkGet(rows.map((r) => r.id));
    const merged = rows.map((row, i) =>
      existing[i] ? { ...row, processedVersion: existing[i].processedVersion } : row
    );
    await db.messages.bulkPut(merged);
  });
}

async function getThreadMessages(threadId) {
  if (!threadId) {
    return [];
  }
  const rows = await db.messages.where('threadId').equals(threadId).sortBy('date');
  return rows;
}

// Messages not yet processed with the current rule-set version.
async function getUnprocessedMessages(chunkSize = 200, cursor = 0) {
  const rows = await db.messages
    .orderBy('date')
    .filter((m) => (m.processedVersion ?? null) !== PROCESSED_VERSION)
    .offset(cursor)
    .limit(chunkSize)
    .toArray();
  return rows;
}

async function countUnprocessedMessages() {
  return db.messages
    .filter((m) => (m.processedVersion ?? null) !== PROCESSED_VERSION)
    .count();
}

async function markMessagesProcessed(ids) {
  if (!ids || ids.length === 0) {
    return;
  }
  await db.messages.where('id').anyOf(ids).modify({ processedVersion: PROCESSED_VERSION });
}

// Partial update of one cached message (e.g. attachmentsParsed/attachmentText).
async function updateMessage(id, fields) {
  await db.messages.update(id, fields);
}

async function getMeta(key) {
  const row = await db.meta.get(key);
  return row ? row.value : null;
}

async function setMeta(key, value) {
  await db.meta.put({ key, value });
}

async function countMessages() {
  return db.messages.count();
}

// All cached messages, oldest first (used to build the local search index).
async function getAllMessages() {
  return db.messages.orderBy('date').toArray();
}

async function clearCache() {
  await db.messages.clear();
  await db.meta.clear();
}

// Ask the browser to exclude this origin's data from storage-pressure
// eviction. Best-effort; resolves true/false.
async function requestPersistence() {
  try {
    if (navigator.storage && navigator.storage.persist) {
      return await navigator.storage.persist();
    }
  } catch (e) {
    console.warn('storage.persist() failed', e);
  }
  return false;
}

export {
  PROCESSED_VERSION,
  saveMessages,
  getThreadMessages,
  getUnprocessedMessages,
  countUnprocessedMessages,
  markMessagesProcessed,
  updateMessage,
  getMeta,
  setMeta,
  countMessages,
  getAllMessages,
  clearCache,
  requestPersistence,
};
